import {
  Hook,
  LOG_LEVEL_OPTIONS,
  STATUS_CODE_OPTIONS,
} from '@rossum/api-client/hooks';
import { useIntl } from 'react-intl';
import { z } from 'zod';
import {
  convertFromUTC,
  convertToUTC,
  formatDateHoursSecondsToQuery,
  formatDateTimeWithSecondsPickerValue,
} from '../../../../components/Datepicker/helpers';
import { FilterDateRange } from '../../../filtering/components/FilterDateRange';
import { FilterMultiSelect } from '../../../filtering/components/FilterMultiSelect';
import { FilterSelect } from '../../../filtering/components/FilterSelect';
import { createSettings } from '../../../filtering/helpers';

const PAGE_SIZE_OPTIONS = ['5', '10', '25', '50', '100'];

export const logsQueryParser = z.object({
  timestampAfter: z.coerce.string().optional(),
  timestampBefore: z.coerce.string().optional(),
  search: z.coerce.string().optional(),
  pageSize: z.coerce.number().optional(),
  logLevel: z.enum(LOG_LEVEL_OPTIONS).optional().catch(undefined),
  hook: z.coerce
    .string()
    .transform(val => val.split(',').map(Number))
    .pipe(z.number().array())
    .optional()
    .catch(undefined),
  statusCode: z.coerce
    .string()
    .transform(val => val.split(','))
    .pipe(z.enum(STATUS_CODE_OPTIONS).array())
    .optional()
    .catch(undefined),
});

export type LogsQuery = z.infer<typeof logsQueryParser>;

export const useLogsFilterSettings = ({
  extensions,
  isLoadingExtensions,
  setFilters,
}: {
  extensions: Hook[] | undefined;
  isLoadingExtensions: boolean;
  setFilters: (state: LogsQuery) => void;
}) => {
  const intl = useIntl();

  return [
    createSettings({
      filterKey: 'date-range',
      options: [],
      name: intl.formatMessage({
        id: 'containers.settings.extensions.logs.filters.extension.dateRange',
      }),
      getChipLabel: v =>
        v[0] && v[1]
          ? formatDateTimeWithSecondsPickerValue(v[0], v[1], intl.locale)
          : 'N/A',
      valueGetter: ({ timestampAfter, timestampBefore }: LogsQuery) => {
        if (!timestampAfter || !timestampBefore) return undefined;

        const beginDate = timestampAfter
          ? convertFromUTC(new Date(timestampAfter))
          : null;
        const endDate = timestampBefore
          ? convertFromUTC(new Date(timestampBefore))
          : null;

        return [beginDate, endDate] as [Date | null, Date | null];
      },
      setFilter: value => {
        if (!value)
          return setFilters({
            timestampAfter: undefined,
            timestampBefore: undefined,
          });

        const [from, to] = value;

        return setFilters({
          timestampAfter: from
            ? formatDateHoursSecondsToQuery(convertToUTC(from))
            : undefined,
          timestampBefore: to
            ? formatDateHoursSecondsToQuery(convertToUTC(to))
            : undefined,
        });
      },
      Component: props => <FilterDateRange {...props} isRange />,
    }),
    createSettings({
      filterKey: 'pageSize',
      options: PAGE_SIZE_OPTIONS,
      optionsAreLoading: false,
      name: intl.formatMessage({
        id: 'containers.settings.extensions.logs.filters.pageSize.label',
      }),
      getChipLabel: v => v.toString(),
      valueGetter: (currentValue: LogsQuery) => currentValue?.pageSize,
      setFilter: value => setFilters({ pageSize: value }),
      Component: FilterSelect,
    }),
    createSettings({
      filterKey: 'logLevel',
      options: [...LOG_LEVEL_OPTIONS],
      optionsAreLoading: false,
      name: intl.formatMessage({
        id: 'containers.settings.extensions.logs.filters.logLevel.label',
      }),
      getChipLabel: v => v,
      valueGetter: (currentValue: LogsQuery) => currentValue?.logLevel,
      setFilter: value => setFilters({ logLevel: value }),
      Component: FilterSelect,
    }),
    createSettings({
      filterKey: 'hook',
      options: extensions?.map(h => h.id.toString()) ?? [],
      optionsAreLoading: isLoadingExtensions,
      getOptionLabel: option =>
        extensions?.find(h => h.id === Number(option))?.name ?? '',
      getChipLabel: value =>
        extensions
          ?.filter(e => value?.includes(e.id))
          .map(e => e.name)
          .join(', ') ?? '',
      name: intl.formatMessage({
        id: 'containers.settings.extensions.logs.filters.extension.label',
      }),
      valueGetter: (currentValue: LogsQuery) => currentValue?.hook,
      setFilter: value => setFilters({ hook: value }),
      Component: FilterMultiSelect,
    }),
    createSettings({
      filterKey: 'statusCode',
      options: [...STATUS_CODE_OPTIONS],
      optionsAreLoading: false,
      name: intl.formatMessage({
        id: 'containers.settings.extensions.logs.filters.statusCode.label',
      }),
      getChipLabel: value =>
        [...STATUS_CODE_OPTIONS].filter(o => value.includes(o)).join(', '),
      valueGetter: (currentValue: LogsQuery) => currentValue?.statusCode,
      setFilter: value => setFilters({ statusCode: value }),
      Component: FilterMultiSelect,
    }),
  ];
};
